import { useEffect, useState } from 'react';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import { FaTrash, FaPlus, FaSave } from 'react-icons/fa';
import DeleteModal from './modal/DeleteModal';
import { createExample, updateExample, deleteExample } from '../../store/template-examples';
import { toast } from 'react-toastify';

const TemplateExample = ({ isOpen, onClose, template, setTemplate, ownerType, fetchById }) => {
  const [examplesList, setExamplesList] = useState([]);
  const [newExample, setNewExample] = useState({ name: '', value: '' });
  const [editStates, setEditStates] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedExample, setSelectedExample] = useState(null);

  useEffect(() => {
    if (template && template.examples) {
      setExamplesList(template.examples);
      const initialEditStates = {};
      template.examples.forEach(example => {
        initialEditStates[example.id] = {
          originalValue: example.value,
          originalName: example.name,
          hasChanges: false
        };
      });
      setEditStates(initialEditStates);
    }
  }, [template]);
  useBodyScrollLock(isOpen);

  const handleChange = (exampleId, field, value) => {
    setExamplesList(prevList =>
      prevList.map(example =>
        example.id === exampleId ? { ...example, [field]: value } : example
      )
    );

    setEditStates(prev => {
      const prevState = prev[exampleId];
      const updatedExample = examplesList.find(ex => ex.id === exampleId) || {};
      const newName = field === 'name' ? value : updatedExample.name;
      const newValue = field === 'value' ? value : updatedExample.value;
      return {
        ...prev,
        [exampleId]: {
          ...prevState,
          hasChanges:
            newName.trim() !== prevState.originalName ||
            newValue.trim() !== prevState.originalValue
        }
      };
    });
  };

  const handleNewChange = (field, value) => {
    setNewExample(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateExample = async () => {
    if (!newExample.name.trim() || !newExample.value.trim()) return;

    setIsLoading(true);
    try {
      const savedExample = await createExample({
        ...newExample,
        owner_id: template.id,
        owner_type: ownerType
      });

      // setExamplesList(prev => [...prev, savedExample]);
      const res = await fetchById(template.id);
      setTemplate(res);

      setNewExample({ name: '', value: '' });

      setEditStates(prev => ({
        ...prev,
        [savedExample.id]: {
          originalValue: savedExample.value,
          originalName: savedExample.name,
          hasChanges: false
        }
      }));

      toast.success('Example added successfully!');
    } catch (error) {
      toast.error('Failed to add example.');
      console.error('Failed to add example:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveExampleChanges = async (exampleId) => {
    const example = examplesList.find(ex => ex.id === exampleId);
    if (!example) return;

    setIsLoading(true);
    try {
      await updateExample(exampleId, {
        name: example.name,
        value: example.value
      });

      const res = await fetchById(template.id);
      setTemplate(res);

      setEditStates(prev => ({
        ...prev,
        [exampleId]: {
          ...prev[exampleId],
          originalValue: example.value,
          originalName: example.name,
          hasChanges: false
        }
      }));

      toast.success('Example updated successfully!');
    } catch (error) {
      toast.error('Failed to update example.');
      console.error('Failed to update example:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteExample = (exampleId) => {
    const example = examplesList.find(ex => ex.id === exampleId);
    setSelectedExample({
      ...example,
      dataName: 'Example'
    });
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async (exampleId) => {
    setIsLoading(true);
    try {
      await deleteExample(exampleId);
      setExamplesList(prev => prev.filter(example => example.id !== exampleId));
      setEditStates(prev => {
        const newEditStates = { ...prev };
        delete newEditStates[exampleId];
        return newEditStates;
      });
      toast.success('Example deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete example.');
      console.error('Failed to delete example:', error);
    } finally {
      setIsLoading(false);
      setIsDeleteModalOpen(false);
      setSelectedExample(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedExample(null);
  };

  const handleShowAddForm = () => {
    setShowAddForm(true);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setNewExample({ name: '', value: '' });
  };

  const handleClose = () => {
    setExamplesList([]);
    setNewExample({ name: '', value: '' });
    setEditStates({});
    setShowAddForm(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="fixed inset-0 bg-black bg-opacity-30" onClick={handleClose}></div>
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-4xl p-6 relative z-10 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mx-1">
          <h2 className="text-xl font-semibold">Manage Template Examples:&nbsp;<span className='text-lg font-normal text-gray-600'>{template.name}</span></h2>
          <div className="flex gap-2">
            {!showAddForm && (
              <button
                onClick={handleShowAddForm}
                className="flex items-center px-4 py-2 border rounded-md shadow-sm focus:outline-none bg-blue-500 text-white"
              >
                <FaPlus className="mr-2" size={12} />
                Add New
              </button>
            )}
            <button
              onClick={handleClose}
              className="px-4 py-2 border rounded-md shadow-sm focus:outline-none bg-gray-500 text-white"
            >
              Close
            </button>
          </div>
        </div>

        <div
          className={`transition-all duration-500 ease-in-out overflow-hidden border-b pb-4 relative ${showAddForm ? 'max-h-[1000px] opacity-100 mb-3' : 'max-h-0 opacity-0 pointer-events-none'
            }`}
          style={{
            transitionProperty: 'max-height, opacity',
            willChange: 'max-height, opacity'
          }}
        >
          {(showAddForm || newExample.name || newExample.value) && (
            <>
              <div className="flex flex-col mb-2 mx-1">
                <label className="block text-gray-700 text-sm font-bold mb-2">Name</label>
                <input
                  type="text"
                  value={newExample.name}
                  onChange={(e) => handleNewChange('name', e.target.value)}
                  className="px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter name"
                />
              </div>

              <div className="flex flex-col mb-4 mx-1">
                <label className="block text-gray-700 text-sm font-bold mb-2">Value</label>
                <textarea
                  value={newExample.value}
                  onChange={(e) => handleNewChange('value', e.target.value)}
                  className="px-4 py-2 border rounded-md min-h-48 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter value"
                />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  onClick={handleCreateExample}
                  disabled={!newExample.name.trim() || !newExample.value.trim() || isLoading}
                  className={`flex items-center px-4 py-2 border rounded-md shadow-sm focus:outline-none bg-blue-500 text-white ${(!newExample.name.trim() || !newExample.value.trim() || isLoading) && 'opacity-80 cursor-not-allowed'
                    }`}
                >
                  <FaPlus className="mr-2" size={12} />
                  Add
                </button>
                <button
                  onClick={handleCancelAdd}
                  className="flex items-center px-4 py-2 border rounded-md shadow-sm focus:outline-none bg-gray-400 text-white"
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>

        <h3 className="text-lg font-medium mb-4 mx-1">Examples List:</h3>

        {examplesList.length === 0 ? (
          <p className="text-gray-500 italic mx-1 h-36 border rounded flex justify-center items-center"><span>No examples available</span></p>
        ) : (
          <div className="space-y-6 mx-1">
            {examplesList.map((example) => (
              <div key={example.id} className="border rounded-md p-4 bg-gray-50">
                <div className="flex flex-col mb-2">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-gray-700 text-sm font-bold">Name</label>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleSaveExampleChanges(example.id)}
                        disabled={!editStates[example.id]?.hasChanges || isLoading}
                        className={`p-1 rounded text-blue-500 hover:bg-blue-100 ${!editStates[example.id]?.hasChanges && 'opacity-60 cursor-not-allowed'
                          }`}
                      >
                        <FaSave size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteExample(example.id)}
                        disabled={isLoading}
                        className="p-1 rounded text-red-500 hover:bg-red-100"
                      >
                        <FaTrash size={16} />
                      </button>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={example.name}
                    onChange={(e) => handleChange(example.id, 'name', e.target.value)}
                    className="px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="block text-gray-700 text-sm font-bold mb-2">Value</label>
                  <textarea
                    value={example.value}
                    onChange={(e) => handleChange(example.id, 'value', e.target.value)}
                    className="px-4 py-2 border rounded-md min-h-48 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onDelete={() => handleConfirmDelete(selectedExample?.id)}
        data={selectedExample || { dataName: 'Example' }}
      />
    </div>
  );
};

export default TemplateExample;